/**
 * @file ProductInfoPanels.tsx
 * @description Панели элементов товара со сведениями (phase19.16 Р2а;
 * uiux-design-r2a §3; SB35, SB36): «Полное описание» (свернуть длинное),
 * «Сведения о товаре» (строками или раскрывающимися пунктами и какие строки
 * показывать — `ProductFactsRowsPanel`; состав — обычная строка списка, без
 * плашки с 2026-10-11; данные — из товара), «Характеристики» (таблицей или
 * строками; заполняются в карточке товара), «Метки» (облаком или строкой),
 * «Продавец» (свёрнутой строкой или полностью; реквизиты — из профиля; с
 * 2026-10-11 не обязателен — реквизиты есть и в юридической строке подвала).
 */

import { Label } from "../../../../ui/label";
import { CheckRow } from "../../../../site-view/CheckRow";
import { Segmented } from "../../../../site-view/Segmented";
import type { ElementPanelProps } from "../types";
import { ProductFactsRowsPanel } from "./ProductFactsRowsPanel";
import { ProductNote } from "./ProductNote";
import { useSampleProductLink, useSellerSettingsLink } from "../../../../../hooks/site-builder/useProductNoteLinks";

export function ProductDescriptionPanel({ element, onChange }: ElementPanelProps<"product-description">) {
  const sample = useSampleProductLink();
  return (
    <>
      <CheckRow checked={element.collapsible} onChange={(collapsible) => onChange({ ...element, collapsible })}>
        Длинное описание свёрнуто, «Читать дальше»
      </CheckRow>
      <ProductNote link={sample}>Текст — из карточки товара. Пусто у товара — на его странице элемента не будет.</ProductNote>
    </>
  );
}

const FACTS_LOOK = [
  { value: "rows" as const, label: "Строками" },
  { value: "accordion" as const, label: "Раскрывающимися пунктами" },
];

export function ProductFactsPanel({ element, onChange }: ElementPanelProps<"product-facts">) {
  const sample = useSampleProductLink();
  return (
    <>
      <Label>Вид</Label>
      <Segmented aria-label="Вид сведений" options={FACTS_LOOK} value={element.look} onChange={(look) => onChange({ ...element, look })} />
      {/* Состав — всегда обычная строка списка (SB35, решение 2026-10-11): галочки плашки нет. */}
      <ProductFactsRowsPanel rows={element.rows} onChange={(rows) => onChange({ ...element, rows })} />
      <ProductNote link={sample}>Сведения берутся из товара: состав, сроки, хранение, производитель, декларация.</ProductNote>
    </>
  );
}

const SPECS_LOOK = [
  { value: "table" as const, label: "Таблицей" },
  { value: "rows" as const, label: "Строками" },
];

export function ProductSpecsPanel({ element, onChange }: ElementPanelProps<"product-specs">) {
  const sample = useSampleProductLink();
  return (
    <>
      <Label>Вид</Label>
      <Segmented aria-label="Вид характеристик" options={SPECS_LOOK} value={element.look} onChange={(look) => onChange({ ...element, look })} />
      <ProductNote link={sample}>Характеристики заполняются в карточке товара. Нет их у товара — элемента на его странице не будет.</ProductNote>
    </>
  );
}

const TAGS_LOOK = [
  { value: "cloud" as const, label: "Облаком" },
  { value: "row" as const, label: "Строкой" },
];

export function ProductTagsPanel({ element, onChange }: ElementPanelProps<"product-tags">) {
  return (
    <>
      <Label>Вид</Label>
      <Segmented aria-label="Вид меток" options={TAGS_LOOK} value={element.look} onChange={(look) => onChange({ ...element, look })} />
      <ProductNote>Метки — из товара; нажатие ведёт в каталог с этой меткой.</ProductNote>
    </>
  );
}

const SELLER_LOOK = [
  { value: "compact" as const, label: "Свёрнуто «Продавец: …»" },
  { value: "full" as const, label: "Полностью" },
];

export function ProductSellerPanel({ element, onChange }: ElementPanelProps<"product-seller">) {
  const link = useSellerSettingsLink();
  return (
    <>
      <Label>Вид</Label>
      <Segmented aria-label="Вид сведений о продавце" options={SELLER_LOOK} value={element.look} onChange={(look) => onChange({ ...element, look })} />
      <ProductNote link={link}>Реквизиты магазина — из профиля.</ProductNote>
      <ProductNote>Реквизиты продавца также есть в юридической строке подвала на каждой странице — элемент можно убрать.</ProductNote>
    </>
  );
}
